import { useTranslation } from 'react-i18next';
import { usePublicContent } from './use-public-content';

export type SectionKey = 'pillars' | 'services' | 'process' | 'projects' | 'trust' | 'contact';

export interface SectionCopy {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
}

/**
 * Heading copy for a landing section, editable from the admin panel.
 *
 * A stored value wins even when empty, so clearing a subtitle in the admin
 * actually removes it instead of silently restoring the built-in text. The
 * translations remain the fallback for anything never stored.
 */
export function useSectionCopy(section: SectionKey, fallback: SectionCopy): SectionCopy {
  const { settings } = usePublicContent();
  const { i18n } = useTranslation();
  const lang = i18n.resolvedLanguage === 'en' ? 'En' : 'De';

  const pick = (field: keyof SectionCopy) => {
    const stored = settings[`section.${section}.${field}${lang}`];
    return stored !== undefined ? stored : fallback[field];
  };

  return { eyebrow: pick('eyebrow'), title: pick('title'), subtitle: pick('subtitle') };
}

/** Every key the admin form edits, in display order. */
export const SECTION_KEYS: SectionKey[] = [
  'pillars',
  'services',
  'process',
  'projects',
  'trust',
  'contact',
];
export const SECTION_FIELDS = ['eyebrow', 'title', 'subtitle'] as const;

export const sectionSettingKey = (
  section: SectionKey,
  field: (typeof SECTION_FIELDS)[number],
  lang: 'De' | 'En',
): string => `section.${section}.${field}${lang}`;
